import API from "../../models/API";
import { ClientValidationOperator } from "../../models/config/ClientValidationOperator";
import { ValidationError } from "../../models/validation/ValidationResult";
import { ValidationRule } from "../../models/validation/ValidationRule";
import { ValidationRuleSet } from "../../models/validation/ValidationRuleSet";

export { ValidationRuleTester, TestedValidationResult };

type ValidationResultData = {
  valid: boolean;
  errors: ValidationError[];
  rules?: ValidationRuleSet;
};

type TestedValidationResult = {
  valid: boolean;
  /**
   * Rule ID to validation error
   */
  errors: { [key in string]: ValidationError };
};

/**
 * Take in a validatoin rule set, return tested results.
 * The rules passed into this method will have UUIDs generated to allow the ValidationError to be linked
 * to the result when returned from the API.
 * The response then has the UUIDs stripped from the returned ValidationRules.
 *
 * To access the result of each rule passed into this classes constructor, the return object can be checked like
 * @description
 * <code>
 * new ValidationRuleTester(rules,'OR').validate('test value').errors[rules[0].getRuleId()]
 * </code>
 */
class ValidationRuleTester {
  ruleset: ValidationRuleSet;
  newUuidToOld: { [x in string]: string | undefined };
  constructor(rules: ValidationRule[], operator: ClientValidationOperator) {
    this.ruleset = {
      fieldName: "",
      clientValidationOperator: operator,
      validationRules: rules.map((x) => new ValidationRule(x.toData())),
    };
    this.newUuidToOld = {};
    const szlastComponent = "000000000000".length;
    for (const x of this.ruleset.validationRules) {
      const i = this.ruleset.validationRules.indexOf(x);
      // create valid UUID for all validation rules
      const newUuid =
        "00000000-0000-0000-0000-" +
        this.pad(parseInt(i + "", 16) + "", szlastComponent);
      this.newUuidToOld[newUuid] = x.uuid;
      x.uuid = newUuid;
    }
  }
  private pad(val: string, num: number): string {
    let ret = val;
    while (ret.length < num) {
      ret = "0" + ret;
    }
    return ret;
  }
  /**
   * Convert response into TestedValidationResult.
   * - removes errors without a validation rule
   * - reassigns
   *
   * @param value
   * @returns
   */
  private convertResponse(value: ValidationResultData): TestedValidationResult {
    // remove garbage without Validation Rule
    const updatedErrors = value.errors
      .filter((x) => x.validationRule)
      //  for each error clone the validation rule
      .map((err) => {
        err.validationRule = new ValidationRule(err.validationRule!);
        return err;
      });

    return {
      errors: updatedErrors.reduce(
        (curr, prev, arr) => {
          // reset UUID so getRuleId() returns same result
          const oldUuid = this.newUuidToOld[prev.validationRule!.uuid ?? ""];
          prev.validationRule!.uuid = oldUuid;
          //  = undefined;
          curr[prev.validationRule!.getRuleId()] = prev;
          return curr;
        },
        {} as TestedValidationResult["errors"],
      ),
      valid: value.valid,
    };
  }

  async validate(
    testValue: string,
  ): Promise<TestedValidationResult | undefined> {
    return API.post(
      `/v0/validate/test`,
      {
        testValue: encodeURIComponent(testValue),
      },
      this.ruleset,
    )
      .then((value: Response) => value.json())
      .then(
        (value: ValidationResultData): TestedValidationResult | undefined => {
          if (value) {
            return this.convertResponse(value);
          }
          return undefined;
        },
      )
      .catch((error: string) => {
        console.log(error);
        return undefined;
      });
  }
}
